/**
 * Table Component
 * 
 * @description
 * Data table component with sorting support and iGaming styling.
 * Supports custom column rendering and responsive overflow handling.
 * 
 * @module components/ui/Table
 */

import { cn } from '@/lib/utils';

export interface TableColumn<T> {
  /** Column key for data access */
  key: keyof T | string;
  /** Column header label */
  header: string;
  /** Custom cell renderer */
  render?: (item: T, index: number) => React.ReactNode;
  /** Whether column is sortable */
  sortable?: boolean;
  /** Column width */
  width?: string;
}

export interface TableProps<T> {
  /** Table data items */
  data: T[];
  /** Column definitions */
  columns: TableColumn<T>[];
  /** Key extractor for rows */
  getKey: (item: T) => string;
  /** Empty state message */
  emptyMessage?: string;
  /** Loading state */
  loading?: boolean;
  /** Row click handler */
  onRowClick?: (item: T) => void;
  /** Additional wrapper class */
  className?: string;
}

/**
 * Table Component
 * 
 * @example
 * ```tsx
 * const columns: TableColumn<Tenant>[] = [
 *   { key: 'name', header: 'Name' },
 *   { key: 'status', header: 'Status', render: (t) => <Badge>{t.status}</Badge> },
 * ];
 * 
 * <Table data={tenants} columns={columns} getKey={(t) => t.id} />
 * ```
 */
export function Table<T>({
  data,
  columns,
  getKey,
  emptyMessage = 'No data available',
  loading = false,
  onRowClick,
  className,
}: TableProps<T>): React.ReactElement {
  /**
   * Render loading skeleton
   */
  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-12 bg-background-tertiary rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
        <svg
          className="w-12 h-12 mb-4 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn('table-wrapper', className)}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={String(column.key)}
                style={column.width ? { width: column.width } : undefined}
                className={cn(
                  column.sortable && 'cursor-pointer hover:text-foreground'
                )}
              >
                <div className="flex items-center gap-2">
                  {column.header}
                  {column.sortable && (
                    <svg
                      className="w-4 h-4 opacity-50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                      />
                    </svg>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={getKey(item)}
              onClick={() => onRowClick?.(item)}
              className={cn(
                onRowClick && 'cursor-pointer'
              )}
            >
              {columns.map((column) => (
                <td key={String(column.key)}>
                  {column.render
                    ? column.render(item, index)
                    : String((item as Record<string, unknown>)[column.key as string] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

Table.displayName = 'Table';